import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import cep from "cep-promise";
import moment from "moment";
import { CircularProgress } from "@mui/material";

import { useUser } from "../../../providers/user";
import { useAuth } from "../../../providers/auth";

import { IRegisterForm, IUserEditForm } from "../../../types/forms";

import states from "../../../utils/states";
import { validateBirthDate, validateCpf } from "../../../utils/formValidations";
import {
  cepMask,
  cpfMask,
  dateMask,
  phoneMask,
} from "../../../utils/formMasks";

import FormInput from "../../../components/Elements/FormInput";
import Termos from "../../Modals/Termos";

const UserForm = () => {
  const navigate = useNavigate();

  const { token } = useAuth();
  const { registerUser, editUserData, userData } = useUser();

  const [cepValue, setCepValue] = useState(token ? userData?.addressCEP : "");
  const [modal, setModal] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const registerSchema = yup.object().shape({
    email: yup
      .string()
      .email("*Formato inválido.")
      .required("*Campo obrigatório."),

    emailConfirmation: yup
      .string()
      .oneOf(
        [yup.ref("email")],
        "*E-mails diferentes, por favor confirme seu e-mail."
      )
      .required("*Campo obrigatório."),

    password: yup
      .string()
      .required("*Campo obrigatório.")
      .matches(
        RegExp(
          "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{6,})(?=.*[!.@#$%^&*\"'¨()_+=`´{}~,<>;:?|\\]\\\\/[-])"
        ),
        "*Utilize letras maiúsculas e minúsculas, números e caracteres especiais (*#.!@)"
      ),

    passwordConfirmation: yup
      .string()
      .oneOf(
        [yup.ref("password")],
        "*Senhas diferentes, por favor confirme sua senha."
      )
      .required("*Campo obrigatório."),

    name: yup
      .string()
      .required("*Campo obrigatório.")
      .matches(
        /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
        "*O nome só pode conter letras do alfabeto latino."
      )
      .matches(
        /^\s*[\S]+(\s[\S]+)+\s*$/gms,
        "*Preencha com seu nome e sobrenome."
      ),

    sex: yup.string().nullable(true).required("*Campo obrigatório."),

    cpf: yup
      .string()
      .required("*Campo obrigatório.")
      .test("cpfValidator", "*CPF inválido", validateCpf),

    rg: yup.string().required("*Campo obrigatório."),

    birthDate: yup
      .string()
      .required("*Campo obrigatório.")
      .transform((value) =>
        value ? moment(value, "DD/MM/YYYY").format("YYYY-MM-DD") : ""
      )
      .test("birthDateValidator", "*Data inválida", validateBirthDate),

    mobilePhone: yup.string().required("*Campo obrigatório."),
    addressCEP: yup.string().required("*Campo obrigatório."),
    addressRoad: yup.string().required("*Campo obrigatório."),
    addressNumber: yup.string().required("*Campo obrigatório."),
    addressComplement: yup.string(),
    addressDistrict: yup.string().required("*Campo obrigatório."),
    addressCity: yup.string().required("*Campo obrigatório."),

    addressState: yup
      .string()
      .test("selectState", "*Selecione um Estado", (inputValue) =>
        inputValue === "selecione" ? false : true
      ),

    acceptTerms: yup
      .boolean()
      .oneOf([true], "*Concorde com os termos de uso para se cadastrar."),

    termsOfUse: yup.string(),
  });

  const UserEditSchema = yup.object().shape({
    email: yup
      .string()
      .email("*Formato inválido.")
      .required("*Campo obrigatório."),

    emailConfirmation: yup
      .string()
      .oneOf(
        [yup.ref("email")],
        "*E-mails diferentes, por favor confirme seu e-mail."
      )
      .required("*Campo obrigatório."),

    password: yup
      .string()
      .nullable()
      .matches(
        RegExp(
          "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{6,})(?=.*[!.@#$%^&*\"'¨()_+=`´{}~,<>;:?|\\]\\\\/[-])"
        ),
        {
          message:
            "*Utilize letras maiúsculas e minúsculas, números e caracteres especiais (*#.!@)",
          excludeEmptyString: true,
        }
      ),

    passwordConfirmation: yup
      .string()
      .oneOf(
        [yup.ref("password")],
        "*Senhas diferentes, por favor confirme sua senha."
      ),

    name: yup
      .string()
      .required("*Campo obrigatório.")
      .matches(
        /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
        "*O nome só pode conter letras do alfabeto latino."
      )
      .matches(
        /^\s*[\S]+(\s[\S]+)+\s*$/gms,
        "*Preencha com seu nome e sobrenome."
      ),

    sex: yup.string().nullable(true).required("*Campo obrigatório."),

    cpf: yup
      .string()
      .required("*Campo obrigatório.")
      .test("cpfValidator", "*CPF inválido", validateCpf),
    rg: yup.string().required("*Campo obrigatório."),

    birthDate: yup
      .string()
      .required("*Campo obrigatório.")
      .transform((value) =>
        value ? moment(value, "DD/MM/YYYY").format("YYYY-MM-DD") : ""
      )
      .test("birthDateValidator", "*Data inválida", validateBirthDate),

    mobilePhone: yup.string().required("*Campo obrigatório."),
    addressCEP: yup.string().required("*Campo obrigatório."),
    addressRoad: yup.string().required("*Campo obrigatório."),
    addressNumber: yup.string().required("*Campo obrigatório."),
    addressComplement: yup.string(),
    addressDistrict: yup.string().required("*Campo obrigatório."),
    addressCity: yup.string().required("*Campo obrigatório."),

    addressState: yup
      .string()
      .test("selectState", "*Selecione um Estado", (inputValue) =>
        inputValue === "selecione" ? false : true
      ),

    termsOfUse: yup.string().default(userData?.termsOfUse),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    reset,
    formState: { errors },
  } = useForm<IRegisterForm>({
    resolver: yupResolver(!!token ? UserEditSchema : registerSchema),
  });

  const onSubmit = (data: IRegisterForm | IUserEditForm) => {
    if (token) {
      return editUserData(data as IUserEditForm, setIsLoading);
    }
    return registerUser(data as IRegisterForm, navigate, setIsLoading);
  };

  const checkCep = async (value: string = cepValue) => {
    try {
      const cepResponse = await cep(value);

      setValue("addressRoad", cepResponse.street);
      setValue("addressDistrict", cepResponse.neighborhood);
      setValue("addressCity", cepResponse.city);
      setValue("addressState", cepResponse.state);
      setFocus("addressNumber");
    } catch (error) {
      console.error(error);
      setFocus("addressCEP");
    }
  };

  const handleCepInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const masked = cepMask(event.target.value);

    setCepValue(masked);
    setValue("addressCEP", masked);

    // CEP completo: preenche o endereço automaticamente
    if (masked.replace(/\D/g, "").length === 8) {
      checkCep(masked);
    }
  };

  const handleTermsOfUseInput = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsAccepted(!isAccepted);

    if (event.target.checked) {
      setValue("termsOfUse", moment().format());
    }
  };

  const agreeToTerms = () => {
    setIsAccepted(true);
    setModal(false);
    setValue("acceptTerms", true);
    setValue("termsOfUse", moment().format());
  };

  const disagreeToTerms = () => {
    setIsAccepted(false);
    setValue("acceptTerms", false);
    setModal(false);
  };

  useEffect(() => {
    reset();
  }, [userData]);

  return (
    <div className="card text-body form-card p-3 p-md-5">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-section-header">
          <span className="form-step" aria-hidden="true">1</span>
          <div>
            <h2 className="h5 mb-0">Dados de acesso</h2>
            <p className="text-muted small mb-0">
              Você usará o e-mail e a senha para entrar e acompanhar suas consultas.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <FormInput
              name="email"
              register={register}
              id="txtEmail"
              type="email"
              placeholder="E-mail"
              error={errors.email?.message}
              defaultValue={token ? userData?.email : ""}
              autoComplete="email"
            />
          </div>
          <div className="col-md-6 mb-3">
            <FormInput
              name="emailConfirmation"
              register={register}
              id="txtConfirmacaoEmail"
              type="email"
              placeholder="Confirme o E-mail"
              error={errors.emailConfirmation?.message}
              defaultValue={token ? userData?.email : ""}
              autoComplete="email"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <FormInput
              name="password"
              register={register}
              id="txtSenha"
              type="password"
              placeholder="Senha"
              error={errors.password?.message}
              autoComplete="new-password"
              isPasswordInput
            />
            {!errors.password && (
              <small className="form-hint">
                Mín. 6 caracteres, com maiúscula, minúscula, número e símbolo (ex.: #, !, @)
              </small>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <FormInput
              name="passwordConfirmation"
              register={register}
              id="txtSenhaConfirmacao"
              type="password"
              placeholder="Confirme sua senha"
              error={errors.passwordConfirmation?.message}
              autoComplete="new-password"
              isPasswordInput
            />
          </div>
        </div>
        <hr className="form-divider" />
        <div className="form-section-header">
          <span className="form-step" aria-hidden="true">2</span>
          <div>
            <h2 className="h5 mb-0">Informações pessoais</h2>
            <p className="text-muted small mb-0">
              Usadas na comunicação com o profissional de saúde.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <FormInput
              name="name"
              register={register}
              id="txtNome"
              type="text"
              placeholder="Nome"
              error={errors.name?.message}
              defaultValue={token ? userData?.name : ""}
              autoComplete="name"
            />
          </div>
          <fieldset className="col-md-6 mb-3 sexo-field">
            <legend className="sexo-legend">Sexo</legend>
            <div className="d-flex gap-4 align-items-center">
              <div className="form-check mb-0">
                <input
                  className={`form-check-input ${
                    !!errors?.sex && "border-danger"
                  }`}
                  type="radio"
                  value="M"
                  id="masc"
                  {...register("sex")}
                  defaultChecked={token ? userData?.sex === "M" : false}
                />
                <label className="form-check-label" htmlFor="masc">
                  Masculino
                </label>
              </div>
              <div className="form-check mb-0">
                <input
                  className={`form-check-input ${
                    !!errors?.sex && "border-danger"
                  }`}
                  type="radio"
                  value="F"
                  id="fem"
                  {...register("sex")}
                  defaultChecked={token ? userData?.sex === "F" : false}
                />
                <label className="form-check-label" htmlFor="fem">
                  Feminino
                </label>
              </div>
            </div>
            {!!errors?.sex && (
              <div className="text-danger">
                <small>*Selecione uma opção</small>
              </div>
            )}
          </fieldset>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <FormInput
              name="cpf"
              register={register}
              id="txtCPF"
              type="text"
              placeholder="CPF"
              error={errors.cpf?.message}
              onChange={(e) => setValue("cpf", cpfMask(e.target.value))}
              defaultValue={
                !!userData.cpf && !!token ? cpfMask(userData?.cpf) : ""
              }
              inputMode="numeric"
            />
          </div>
          <div className="col-md-6 mb-3">
            <FormInput
              name="rg"
              register={register}
              id="txtRG"
              type="text"
              placeholder="RG"
              error={errors.rg?.message}
              defaultValue={token ? userData?.rg : ""}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <FormInput
              name="birthDate"
              register={register}
              id="txtDtNasc"
              type="text"
              placeholder="Data de Nascimento"
              error={errors.birthDate?.message}
              onChange={(e) => setValue("birthDate", dateMask(e.target.value))}
              defaultValue={
                token ? moment(userData?.birthDate).format("DD/MM/YYYY") : ""
              }
              inputMode="numeric"
            />
          </div>
          <div className="col-md-6 mb-3">
            <FormInput
              name="mobilePhone"
              register={register}
              id="txtTelefoneCelular"
              type="tel"
              placeholder="Celular (para recebimento de SMS)"
              error={errors.mobilePhone?.message}
              onChange={(e) =>
                setValue("mobilePhone", phoneMask(e.target.value))
              }
              defaultValue={
                token
                  ? phoneMask(
                      `${userData.dddMobilePhone}${userData?.mobilePhone}`
                    )
                  : ""
              }
              autoComplete="tel" inputMode="tel"
            />
          </div>
        </div>
        <hr className="form-divider" />
        <div className="form-section-header">
          <span className="form-step" aria-hidden="true">3</span>
          <div>
            <h2 className="h5 mb-0">Endereço</h2>
            <p className="text-muted small mb-0">
              Digite o CEP e o endereço é preenchido automaticamente.
            </p>
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-12 col-md-4 mb-3">
            <FormInput
              name="addressCEP"
              register={register}
              id="txtCep"
              type="text"
              placeholder="CEP"
              inputMode="numeric"
              autoComplete="postal-code"
              error={errors.addressCEP?.message}
              onChange={handleCepInput}
              defaultValue={token ? userData?.addressCEP : ""}
            />
          </div>
          <div className="col-12 col-md-8">
            <div className="mb-3 text-center text-md-start">
              <button
                type="button"
                className="btn btn-primary me-3"
                onClick={() => checkCep()}
              >
                <i className="las la-search me-2" aria-hidden="true"></i>
                Buscar endereço
              </button>
              <a
                href="http://www.buscacep.correios.com.br/sistemas/buscacep/"
                className="btn btn-link p-0 align-baseline"
                target="_blank"
                rel="noreferrer"
              >
                Não sei meu CEP
              </a>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8 mb-3">
            <FormInput
              name="addressRoad"
              register={register}
              id="txtEndereco"
              type="text"
              placeholder="Endereço"
              error={errors.addressRoad?.message}
              defaultValue={token ? userData?.addressRoad : ""}
              autoComplete="address-line1"
            />
          </div>
          <div className="col-12 col-md-4 mb-3">
            <FormInput
              name="addressNumber"
              register={register}
              id="txtNumero"
              type="text"
              placeholder="Número"
              error={errors.addressNumber?.message}
              defaultValue={token ? userData?.addressNumber : ""}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <FormInput
              name="addressComplement"
              register={register}
              id="txtComplemento"
              type="text"
              placeholder="Complemento"
              error={errors.addressComplement?.message}
              defaultValue={token ? userData?.addressComplement : ""}
            />
          </div>
          <div className="col-md-6 mb-3">
            <FormInput
              name="addressDistrict"
              register={register}
              id="txtBairro"
              type="text"
              placeholder="Bairro"
              error={errors.addressDistrict?.message}
              defaultValue={token ? userData?.addressDistrict : ""}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-8 mb-3">
            <FormInput
              name="addressCity"
              register={register}
              id="txtCidade"
              type="text"
              placeholder="Cidade"
              error={errors.addressCity?.message}
              defaultValue={token ? userData?.addressCity : ""}
              autoComplete="address-level2"
            />
          </div>
          <div className="col-12 col-md-4 mb-3">
            <div className="form-floating">
              <select
                id="ddlEstado"
                className={`form-select ${
                  !!errors?.addressState && "border-danger"
                }`}
                defaultValue={token ? userData?.addressState : "selecione"}
                {...register("addressState")}
              >
                <option value="selecione" disabled>
                  Selecione
                </option>
                {states.map((elm, i) => (
                  <option key={i} value={elm.code}>
                    {elm.code}
                  </option>
                ))}
              </select>
              <label className="label" htmlFor="estado">
                Estado
              </label>
              <div className="text-danger">
                <small>
                  {!!errors?.addressState && "*Selecione uma opção"}
                </small>
              </div>
            </div>
          </div>
        </div>

        {!token && (
          <div className={`terms-box ${!!errors?.acceptTerms ? "terms-box-error" : ""}`}>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="cbTermos"
                {...register("acceptTerms")}
                checked={isAccepted}
                onChange={handleTermsOfUseInput}
              />
              <label className="form-check-label" htmlFor="cbTermos">
                Li e concordo com os{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={() => setModal(true)}
                >
                  termos de uso
                </button>
                .
              </label>
            </div>
            {!!errors?.acceptTerms && (
              <div className="text-danger mt-1">
                <small>{errors.acceptTerms.message}</small>
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-4">
          <button
            type="submit"
            className="btn btn-success btn-lg fw-semibold submit-btn"
            disabled={isLoading}
          >
            {!!isLoading ? (
              <CircularProgress size={21} color="inherit" />
            ) : !!token ? (
              <>
                <i className="las la-check me-2" aria-hidden="true"></i>Salvar alterações
              </>
            ) : (
              <>
                <i className="las la-user-plus me-2" aria-hidden="true"></i>Criar minha conta
              </>
            )}
          </button>
          {!token && (
            <p className="text-muted small mt-3 mb-0">
              Seus dados são usados apenas para o atendimento e estão protegidos pela LGPD.
            </p>
          )}
        </div>
      </form>

      <Termos
        modal={modal}
        setModal={setModal}
        agree={agreeToTerms}
        disagree={disagreeToTerms}
      />
    </div>
  );
};

export default UserForm;
